import React, { useMemo } from 'react';
import { Alert, Breadcrumb } from 'antd';
import { useTranslation } from 'react-i18next';
import { useInboundAcceptance } from '@contexts/operator/InboundAcceptanceContext';
import { VerificationFormPanel } from '@components/operator/shared/inventory_verification';
import type {
  ProductFlags,
  VerificationFormValues,
} from '@components/operator/shared/inventory_verification';

interface ReceiptItem {
  id: string;
  quantity?: number;
  box_count?: number | null;
  quantity_per_box?: number | null;
  loose_quantity?: number | null;
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  inventory_type_id?: string | null;
  custom_attributes?: Record<string, unknown> | null;
  note?: string | null;
}

interface ScannedItem {
  id?: string;
  product_id?: string;
  product_name?: string;
  product_sku?: string;
  product_barcode?: string;
  product_image_url?: string;
  product_validity_days?: number;
  batch_management_required?: boolean;
  manufacturing_date_required?: boolean;
  required_serial_numbers_count?: number;
  serial_number_pattern?: string | null;
  box_serial_number_delimiter?: string | null;
  small_quantity_per_large_unit?: number;
  inventory_type_id?: string;
  inventory_type_name?: string;
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  custom_attributes?: Record<string, unknown> | null;
  scheduled_quantity?: number;
  received_quantity?: number;
  received_at?: string | null;
  receipt_items?: ReceiptItem[];
}

interface FormPageProps {
  scannedItem: ScannedItem | null;
  formMode?: 'edit' | 'add_batch' | string;
  editingReceiptItem?: ReceiptItem | null;
  onNavigate: (target: string) => void;
  onSubmitSuccess?: () => void;
}

const FormPage: React.FC<FormPageProps> = ({
  scannedItem,
  formMode,
  editingReceiptItem: editingRI,
  onNavigate,
  onSubmitSuccess,
}) => {
  const { t } = useTranslation();
  const {
    inbound,
    verifyItem,
    createItem,
    attributeDefinitions,
    inventoryTypes,
    submitting,
  } = useInboundAcceptance();

  const isCreateMode = !scannedItem?.id;
  const isEditMode = formMode === 'edit';
  const isAddBatchMode = formMode === 'add_batch';
  const hasBeenVerified = !!scannedItem?.received_at && !isCreateMode;
  const receiptItems = scannedItem?.receipt_items || [];
  const totalReceived = receiptItems.reduce(
    (sum: number, ri: ReceiptItem) => sum + (ri.quantity || 0),
    0,
  );

  // 狀態列的「累計量」比較基準：本次驗收動作之前該品項已收的量。
  // - edit：totalReceived 已包含「正在編輯那筆」，要扣掉它本身，避免重複計入。
  // - add_batch：先前各批都算已收，baseline = totalReceived。
  // - create / 首次驗收：尚無已收量，baseline = 0。
  const baselineReceivedQuantity = isEditMode
    ? Math.max(0, totalReceived - (editingRI?.quantity ?? 0))
    : isAddBatchMode
      ? totalReceived
      : 0;

  const productFlags = useMemo<ProductFlags>(() => {
    const rawCount = scannedItem?.required_serial_numbers_count ?? 0;
    // Merchant policy gate：outbound_only 商家驗收階段不建 PSN（BE
    // VerifyItemService#persist_serial_numbers! 會 silent discard），FE 也必須
    // 不開放序號輸入；做法是把 count 拉成 0 讓 panel 的 needSerialNumber 推導為
    // false。產品本身的 required_serial_numbers_count 不變，只是 context-specific
    // 在驗收這個 stage 不收（出貨/退貨 stage 仍會收）。
    const policy = inbound?.serial_tracking_policy;
    const count = policy === 'outbound_only' ? 0 : rawCount;
    return {
      validity_days: scannedItem?.product_validity_days,
      batch_management_required: scannedItem?.batch_management_required,
      manufacturing_date_required: scannedItem?.manufacturing_date_required,
      required_serial_numbers_count: count,
      serial_number_pattern: scannedItem?.serial_number_pattern ?? null,
      box_serial_number_delimiter: scannedItem?.box_serial_number_delimiter ?? null,
      product_barcode: scannedItem?.product_barcode ?? null,
      small_quantity_per_large_unit: scannedItem?.small_quantity_per_large_unit,
    };
  }, [scannedItem, inbound?.serial_tracking_policy]);

  const initialValues = useMemo<Partial<VerificationFormValues>>(() => {
    if (!scannedItem) return {};
    if (isEditMode && editingRI) {
      // 編輯模式只認 editingRI 自己的值；不可 fall back 到 scannedItem (= 已被
      // BE plan-enrichment 過的 inbound_item)，否則「第一批帶值、第二批留空」的
      // 情境下，編輯第二批會誤把第一批的 batch / expiration_date /
      // manufacturing_date / custom_attributes / inventory_type_id 帶進表單。
      // 參見 verify_item_service.rb:154-161（plan enrichment 來源）。
      // inventory_type_id 對舊 client（BE 還沒 expose ri.inventory_type_id 之前
      // 建立的快照）保留 fallback 到 plan，避免顯示空白。
      return {
        quantity: editingRI.quantity || 0,
        box_count: editingRI.box_count ?? 0,
        quantity_per_box: editingRI.quantity_per_box ?? 0,
        loose_quantity: editingRI.loose_quantity ?? 0,
        batch: editingRI.batch ?? null,
        expiration_date: editingRI.expiration_date ?? null,
        manufacturing_date: editingRI.manufacturing_date ?? null,
        inventory_type_id: editingRI.inventory_type_id ?? scannedItem.inventory_type_id,
        custom_attributes: editingRI.custom_attributes ?? {},
        note: editingRI.note || '',
      };
    }
    const received = scannedItem.received_quantity || 0;
    const remaining = Math.max(0, (scannedItem.scheduled_quantity || 0) - received);
    // 已點收 >= 預計（remaining=0）時預設 0，由使用者明確輸入這批要追加幾件；
    // VerificationFormPanel 會把 submit 按鈕鎖到 computedQuantity>0 才可送出。
    // 舊行為錯誤地 fallback 到 1，會誤導操作員以為「再驗收 1 件」是合理預設。
    const targetQty = remaining;
    const defaultQpb = scannedItem.small_quantity_per_large_unit || 0;
    // 已知每箱入數時，把剩餘可驗收量自動拆成「幾箱 + 幾散」當預設值（例：剩餘
    // 52、入數 24 → 2 箱 + 4 散）。qpb 缺漏（0/null）時退回整批塞散、箱數 0。
    // 不論哪條路徑都滿足 boxCount * defaultQpb + looseQty === targetQty，總驗收數不變。
    const boxCount = defaultQpb > 0 ? Math.floor(targetQty / defaultQpb) : 0;
    const looseQty = targetQty - boxCount * defaultQpb;
    return {
      quantity: targetQty,
      box_count: boxCount,
      quantity_per_box: defaultQpb,
      loose_quantity: looseQty,
      batch: scannedItem.batch ?? null,
      expiration_date: scannedItem.expiration_date ?? null,
      manufacturing_date: scannedItem.manufacturing_date ?? null,
      inventory_type_id: scannedItem.inventory_type_id,
      custom_attributes: scannedItem.custom_attributes || {},
    };
  }, [scannedItem, editingRI, isEditMode]);

  const handleSubmit = async (
    values: VerificationFormValues,
    serials: string[],
  ): Promise<void> => {
    const additionalData = {
      batch: values.batch,
      expiration_date: values.expiration_date,
      manufacturing_date: values.manufacturing_date,
      inventory_type_id: values.inventory_type_id,
      custom_attributes: values.custom_attributes ?? null,
      serial_numbers: serials,
      ...(editingRI ? { receipt_item_id: editingRI.id } : {}),
      received_box_count: values.box_count,
      received_quantity_per_box: values.quantity_per_box,
      received_loose_quantity: values.loose_quantity,
      note: values.note || null,
    };

    const success = scannedItem?.id
      ? await verifyItem(scannedItem.id, values.quantity, additionalData)
      : await createItem({
        product_id: scannedItem?.product_id,
        received_quantity: values.quantity,
        ...additionalData,
      });

    if (success) {
      onSubmitSuccess?.();
    }
  };

  const breadcrumbItems: Array<{ title: React.ReactNode }> = [];
  if (hasBeenVerified) {
    breadcrumbItems.push({
      title: <a onClick={() => onNavigate('mode')}>{t('operator.inbound_item.select_mode')}</a>,
    });
    breadcrumbItems.push({
      title: isEditMode
        ? t('operator.inbound_item.mode_edit')
        : t('operator.inbound_item.mode_add_batch'),
    });
  }

  return (
    <div>
      {breadcrumbItems.length > 0 && (
        <Breadcrumb style={{ marginBottom: 16 }} items={breadcrumbItems} />
      )}

      {isEditMode && (
        <Alert
          message={t('operator.inbound_item.mode_edit_hint')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {isAddBatchMode && (
        <Alert
          message={t('operator.inbound_item.mode_add_batch_hint')}
          description={t('operator.inbound_item.existing_receipts', { count: receiptItems.length, quantity: totalReceived })}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {isCreateMode && (
        <Alert
          message={t('operator.inbound_item.create_mode_hint')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <VerificationFormPanel
        productFlags={productFlags}
        initialValues={initialValues}
        quantityMode="box-calc"
        scheduledQuantity={scannedItem?.scheduled_quantity}
        baselineReceivedQuantity={baselineReceivedQuantity}
        attributeDefinitions={attributeDefinitions || []}
        inventoryTypes={(inventoryTypes || []).map((it: {
          inventory_type_id: string;
          inventory_type?: { name?: string; full_name?: string };
        }) => ({
          id: it.inventory_type_id,
          name: it.inventory_type?.name || it.inventory_type?.full_name,
        }))}
        showNote
        loading={submitting}
        onSubmit={handleSubmit}
        onCancel={() => onNavigate(hasBeenVerified ? 'mode' : 'close')}
        cancelButtonLabel={
          hasBeenVerified
            ? (t('common.button.back', { defaultValue: '返回' }) as string)
            : (t('common.button.cancel', { defaultValue: '取消' }) as string)
        }
      />
    </div>
  );
};

export default FormPage;
